import * as React from 'react';
import TranslateWithComponents from '@sdk/lang/TranslateWithComponents';
import CollapsibleSection from '@sdk/components/ui/CollapsibleSection';
import Column from '@sdk/components/ui/spacing/Column';
import Row from '@sdk/components/ui/spacing/Row';
import Text14 from '@sdk/components/ui/typography/Text14';
import gettext, { ngettext } from '@sdk/lang/gettext';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';

import SettingsSection from '../../../settings/components/SettingsSection';
import SettingsSwitcherRow from '../../../settings/components/SettingsSwitcherRow';
import { CustomTextField } from '../components/CustomTextField';
import { FIELD_WIDTH_150 } from '../../../settings/constants';

import type {
  ClockInAutoClockoutEnabledField,
  ClockInAutoClockoutMinutesField,
} from './types/TimeclockFields';

type Props = {
  clockInAutoClockoutEnabledField: ClockInAutoClockoutEnabledField;
  clockInAutoClockoutMinutesField: ClockInAutoClockoutMinutesField;
};

const AutomaticClockOut: React.FC<React.PropsWithChildren<Props>> = ({
  clockInAutoClockoutEnabledField,
  clockInAutoClockoutMinutesField,
}) => {
  return (
    <SettingsSection pBottom={20}>
      <CollapsibleSection
        isActive={clockInAutoClockoutEnabledField.value}
        headerItem={
          <SettingsSwitcherRow
            title={gettext('Automatic clock out')}
            subtitle={gettext(
              'Automatically clock employees out after the scheduled end of the shift'
            )}
            value={clockInAutoClockoutEnabledField.value}
            onChange={(value): void => {
              clockInAutoClockoutEnabledField.onChange(value);
              !value && clockInAutoClockoutMinutesField.dirty
                ? clockInAutoClockoutMinutesField.reset()
                : null;
            }}
          />
        }
        contentItem={
          <Column pTop={20}>
            <Row alignItems="center" justifyContent="space-between">
              <Row>
                <Text14 themeColor={ThemeColorType.gray}>
                  <TranslateWithComponents
                    replaceComponents={{
                      component: (
                        <Text14 themeColor={ThemeColorType.primary}>
                          {clockInAutoClockoutMinutesField.value}
                        </Text14>
                      ),
                    }}>
                    {ngettext(
                      'Clock employees out [[component]] minute after shift ends',
                      'Clock employees out [[component]] minutes after shift ends',
                      Number(clockInAutoClockoutMinutesField.value)
                    )}
                  </TranslateWithComponents>
                </Text14>
              </Row>

              <CustomTextField
                width={FIELD_WIDTH_150}
                skipErrorMessage
                postfix={gettext('Minutes')}
                value={clockInAutoClockoutMinutesField.value}
                onChange={(event): void =>
                  clockInAutoClockoutMinutesField.onChange(Number(event.currentTarget.value) || 0)
                }
                onBlur={clockInAutoClockoutMinutesField.onBlur}
                invalid={!!clockInAutoClockoutMinutesField.error}
              />
            </Row>
          </Column>
        }
      />
    </SettingsSection>
  );
};

export default AutomaticClockOut;
